// The one confirmation every identity merge on a face match goes through
// (ADR-0042): the star, the candidate table and the document overlay all open it.
// It is also where the analyst may swap which of the two stays (ADR-0052).
import { ArrowLeftRight, ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { FaceTile } from './FaceTile'

const PM = 'cases.investigate.entityResolution.photoMatching'

/** One side of a merge, as far as the caller's read already knows it. */
export interface MergeIdentity {
  /** The identity's id: it tells the caller which of the two was confirmed to stay. */
  personId: string
  /** Null reads as the unknown-identity fallback. */
  displayName: string | null
  /** The main photo: an auth-gated files URL, which `FaceTile` resolves. */
  photoUrl: string | null
}

/** The two identities a face-match merge joins, and which one folds into which. */
export interface MergeIdentitiesPair {
  /** Folded away — as the merge is proposed, the identity the face is already confirmed to. */
  merged: MergeIdentity
  /** Kept — as the merge is proposed, the identity the face is suggested for. */
  survivor: MergeIdentity
}

export interface MergeIdentitiesDialogProps {
  /** The merge to ask about, the way it is proposed; null keeps the dialog closed. */
  pair: MergeIdentitiesPair | null
  /**
   * "Merge": the caller runs the merge as it was *confirmed* — the proposed pair,
   * or the same two the other way round once the analyst swapped them. The dialog
   * then closes on its own.
   */
  onConfirm: (confirmed: MergeIdentitiesPair) => void
  /** The dialog closed — cancelled, dismissed, or after a confirm. */
  onClose: () => void
}

/**
 * Ask before an identity merge: which identity is folded into which, what moves
 * with it, and that the entity-resolution audit can undo it. The analyst may swap
 * the two, and the merge is confirmed the way the dialog shows it. Pure — the
 * caller owns the pair and runs the merge.
 */
export function MergeIdentitiesDialog({ pair, onConfirm, onClose }: MergeIdentitiesDialogProps) {
  const { t } = useTranslation()
  const [swapped, setSwapped] = useState(false)
  // A closed dialog forgets the swap, however it closed: the next merge is asked
  // about the way it is proposed.
  if (pair === null && swapped) setSwapped(false)
  const asked = pair && swapped ? { merged: pair.survivor, survivor: pair.merged } : pair

  return (
    <AlertDialog
      open={asked !== null}
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <AlertDialogContent>
        {/* Below lg the header may shrink under its content, so two long names
            truncate instead of pushing the dialog's body and buttons out of it. */}
        <AlertDialogHeader className="max-lg:min-w-0">
          <AlertDialogTitle>{t(`${PM}.merge.title`)}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="max-lg:w-full max-lg:min-w-0">
              {asked ? (
                <MergeIdentitiesSummary
                  pair={asked}
                  onSwap={() => setSwapped(current => !current)}
                />
              ) : null}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (asked) onConfirm(asked)
            }}
          >
            {t(`${PM}.merge.confirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export interface MergeIdentitiesSummaryProps {
  pair: MergeIdentitiesPair
  /** Swap which identity goes and which stays. */
  onSwap: () => void
}

/**
 * Who is folded into whom, what moves with it and how to take it back. Split out
 * of the dialog so it renders on its own — the dialog's portal never reaches a
 * static render.
 */
export function MergeIdentitiesSummary({ pair, onSwap }: MergeIdentitiesSummaryProps) {
  const { t } = useTranslation()
  const nameOf = (identity: MergeIdentity) => identity.displayName ?? t(`${PM}.unknownIdentity`)
  const merged = nameOf(pair.merged)
  const survivor = nameOf(pair.survivor)

  return (
    <div className="flex w-full flex-col gap-3" data-merge-summary="">
      {/* Keyed by identity, so a swap moves the two tiles instead of loading their photos again. */}
      <div className="flex items-center justify-center gap-3">
        <MergeSide
          key={pair.merged.personId}
          photoUrl={pair.merged.photoUrl}
          name={merged}
          side="merged"
        />
        <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <MergeSide
          key={pair.survivor.personId}
          photoUrl={pair.survivor.photoUrl}
          name={survivor}
          side="survivor"
        />
      </div>
      <Button
        type="button"
        variant="outline"
        size="xs"
        className="self-center max-md:h-9"
        data-merge-swap=""
        onClick={onSwap}
      >
        <ArrowLeftRight aria-hidden />
        {t(`${PM}.merge.swap`)}
      </Button>
      {/* Said again after a swap: this sentence is what names who goes where. */}
      <p aria-live="polite">{t(`${PM}.merge.description`, { merged, survivor })}</p>
      <p>{t(`${PM}.merge.undo`)}</p>
    </div>
  )
}

function MergeSide({
  photoUrl,
  name,
  side,
}: {
  photoUrl: string | null
  name: string
  side: 'merged' | 'survivor'
}) {
  const { t } = useTranslation()
  return (
    <div data-merge-side={side} className="flex min-w-0 flex-1 flex-col items-center gap-1">
      <FaceTile url={photoUrl} alt={t(`${PM}.comparison.reference.photoAlt`, { name })} />
      <span className="max-w-full truncate font-medium text-foreground">{name}</span>
      <span className="text-2xs uppercase tracking-wider">{t(`${PM}.merge.${side}`)}</span>
    </div>
  )
}
